import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { cn } from '@/lib/utils';
import { Html5Qrcode } from 'html5-qrcode';
import jsQR from 'jsqr';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Loader2, RefreshCw, Search, Volume2, VolumeX, Video, VideoOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';
import { formatContextualDateTime } from '@/lib/date';
import { formatDisplayPhone } from '@/lib/phone-utils';
import { safeStopHtml5Qrcode } from '@/lib/html5-qrcode-scanner';
import {
  createAudioElement,
  playAudio,
  hapticFeedback,
  AUDIO_PATHS,
  AUDIO_VOLUMES,
} from '@/lib/feedback';
import { ScanEntryDialog } from '@/components/events/ScanEntryDialog';
import { FieldBarcodeScannerDialog } from '@/components/events/FieldBarcodeScannerDialog';
import {
  type ActivityScanField,
  type ActivityScanFieldValues,
  createEmptyFieldValues,
  parseActivityScanFields,
} from '@/lib/activity-scan-fields';
import { useTenant } from '@/providers/tenant-context';

interface PendingScanPayload {
  qrValue?: string;
  registrationId?: string;
}

interface ScanPreviewResponse {
  attendee: {
    name: string;
    email: string;
    registrationId: string;
  };
  existingScanLog: {
    id: string;
    fieldValues: ActivityScanFieldValues;
    status?: 'PENDING' | 'CONFIRMED';
  } | null;
  duplicateBehavior: 'REJECT' | 'CREATE_NEW' | 'UPDATE_EXISTING';
  scanEntryFields: ActivityScanField[];
}

interface PendingSelfMark {
  id: string;
  scannedAt: string;
  registrationId: string;
  attendeeName: string;
  attendeeEmail: string;
  attendeePhone: string | null;
  attendeeUserId: string | null;
}

export default function ActivityScannerPage() {
  const { communityId, eventId, activityId } = useParams();
  const { tenant } = useTenant();
  const [lastSuccessfulScan, setLastSuccessfulScan] = useState<string | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isScanning, setIsScanning] = useState(true);
  const [voiceMuted, setVoiceMuted] = useState(false);
  const [cameraClosed, setCameraClosed] = useState(false);
  const voiceMutedRef = useRef(false);
  voiceMutedRef.current = voiceMuted;
  const [confirmingLogId, setConfirmingLogId] = useState<string | null>(null);

  const html5QrContainerId =
    communityId && eventId && activityId
      ? `activity-html5-scan-${communityId}-${eventId}-${activityId}`
      : 'activity-html5-scan-default';

  // iOS frame streaming refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameQueueRef = useRef<string[]>([]);
  const isRenderingRef = useRef(false);

  // Audio Refs
  const successAudio = useRef<HTMLAudioElement | null>(null);
  const errorAudio = useRef<HTMLAudioElement | null>(null);
  const duplicateAudio = useRef<HTMLAudioElement | null>(null);

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 400);

  // Auto-Complete Search
  const { data: searchResults, isLoading: isSearchLoading } = useSWR(
    debouncedSearchQuery.length >= 1 && eventId && activityId
      ? `/api/events/${eventId}/activities/${activityId}/search-attendees?q=${encodeURIComponent(
          debouncedSearchQuery
        )}`
      : null,
    (url) => axiosInstance.get(url).then((res) => res.data.results)
  );

  const [checking, setChecking] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [entrySubmitting, setEntrySubmitting] = useState(false);
  const [pendingScan, setPendingScan] = useState<PendingScanPayload | null>(null);
  const [entryAttendee, setEntryAttendee] = useState<{ name: string; email: string } | null>(null);
  const [entryFieldValues, setEntryFieldValues] = useState<ActivityScanFieldValues>(() =>
    createEmptyFieldValues([])
  );
  const [isUpdatingExisting, setIsUpdatingExisting] = useState(false);
  const [entryDialogFields, setEntryDialogFields] = useState<ActivityScanField[]>([]);
  const [barcodeFieldScan, setBarcodeFieldScan] = useState<{
    fieldId: string;
    fieldLabel: string;
  } | null>(null);
  const shouldRunMainScanner = !entryDialogOpen && !barcodeFieldScan && !cameraClosed;
  const shouldRunMainScannerRef = useRef(shouldRunMainScanner);
  shouldRunMainScannerRef.current = shouldRunMainScanner;

  const playScanAudio = (audio: HTMLAudioElement | null, soundType: string) => {
    if (voiceMutedRef.current) return;
    playAudio(audio, soundType);
  };
  const lastScannedCode = useRef<string | null>(null);
  const lastScannedTime = useRef<number>(0);
  const DUPLICATE_SCAN_WINDOW = 5000;

  const checkingRef = useRef(false);

  const { data: activity } = useSWR(
    eventId && activityId ? `/api/events/${eventId}/activities/${activityId}` : null,
    (url) => axiosInstance.get(url).then((res) => res.data)
  );

  const { data: logsData, mutate: mutateLogs } = useSWR<{
    logs: Array<{
      id: string;
      scannedAt: string;
      attendeeName: string;
      attendeeEmail: string;
      attendeeUserId: string | null;
      fieldValues: ActivityScanFieldValues;
    }>;
    scanEntryFields: ActivityScanField[];
  }>(
    eventId && activityId ? `/api/events/${eventId}/activities/${activityId}/logs` : null,
    (url) => axiosInstance.get(url).then((res) => res.data),
    { refreshInterval: 5000 }
  );

  const { data: pendingData, mutate: mutatePending } = useSWR<{ pending: PendingSelfMark[] }>(
    eventId && activityId
      ? `/api/events/${eventId}/activities/${activityId}/pending-self-marks`
      : null,
    (url) => axiosInstance.get(url).then((res) => res.data),
    { refreshInterval: 3000 }
  );
  const pendingSelfMarks = pendingData?.pending ?? [];
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshScannerData = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([mutateLogs(), mutatePending()]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const logs = logsData?.logs ?? [];
  const scanEntryFields = [activity?.scanEntryFields, logsData?.scanEntryFields]
    .map(parseActivityScanFields)
    .reduce(
      (best, fields) => (fields.length > best.length ? fields : best),
      [] as ActivityScanField[]
    );

  useEffect(() => {
    successAudio.current = createAudioElement(AUDIO_PATHS.success, AUDIO_VOLUMES.success);
    errorAudio.current = createAudioElement(AUDIO_PATHS.error, AUDIO_VOLUMES.error);
    duplicateAudio.current = createAudioElement(AUDIO_PATHS.duplicate, AUDIO_VOLUMES.duplicate);
  }, []);

  const resetCheckingState = (allowRescan = false) => {
    setTimeout(() => {
      checkingRef.current = false;
      setChecking(false);
      setLastSuccessfulScan(null);
      if (allowRescan) {
        lastScannedCode.current = null;
      }
    }, 2000);
  };

  const completeCheckInSuccess = (attendeeName: string, updated?: boolean) => {
    playScanAudio(successAudio.current, 'success');
    setLastSuccessfulScan(attendeeName);
    toast.success(updated ? `Updated: ${attendeeName}` : `Checked in: ${attendeeName}`);
    mutateLogs();
    void mutatePending();
    setEntryDialogOpen(false);
    setEntryDialogFields([]);
    setPendingScan(null);
    setEntryAttendee(null);
    setEntryFieldValues(createEmptyFieldValues(scanEntryFields));
    setBarcodeFieldScan(null);
    resetCheckingState();
  };

  const failCheckIn = (errorMessage: string) => {
    if (errorMessage.toLowerCase().includes('already')) {
      playScanAudio(duplicateAudio.current, 'duplicate');
    } else {
      playScanAudio(errorAudio.current, 'error');
    }
    toast.error(errorMessage);
    setEntryDialogOpen(false);
    setEntryDialogFields([]);
    setPendingScan(null);
    setEntryAttendee(null);
    setEntryFieldValues(createEmptyFieldValues(scanEntryFields));
    setBarcodeFieldScan(null);
    resetCheckingState(true);
  };

  const submitScan = async (payload: PendingScanPayload, fieldValues?: ActivityScanFieldValues) => {
    const response = await axiosInstance.post(
      `/api/events/${eventId}/activities/${activityId}/scan`,
      {
        ...payload,
        ...(fieldValues ? { fieldValues } : {}),
      }
    );
    completeCheckInSuccess(response.data.attendee.name, response.data.updated);
  };

  const beginAttendeeCheckIn = async (payload: PendingScanPayload) => {
    checkingRef.current = true;
    setChecking(true);

    try {
      const preview = await axiosInstance.post<ScanPreviewResponse>(
        `/api/events/${eventId}/activities/${activityId}/scan`,
        { ...payload, preview: true }
      );

      if (
        preview.data.duplicateBehavior === 'REJECT' &&
        preview.data.existingScanLog?.status === 'CONFIRMED'
      ) {
        failCheckIn('Attendee has already been checked in for this activity.');
        return;
      }

      const fields = parseActivityScanFields(preview.data.scanEntryFields);

      if (fields.length === 0) {
        await submitScan(payload);
        return;
      }

      const updating =
        preview.data.duplicateBehavior === 'UPDATE_EXISTING' &&
        Boolean(preview.data.existingScanLog);

      setEntryDialogFields(fields);
      setPendingScan(payload);
      setEntryAttendee({
        name: preview.data.attendee.name,
        email: preview.data.attendee.email,
      });
      setEntryFieldValues(
        updating && preview.data.existingScanLog?.fieldValues
          ? preview.data.existingScanLog.fieldValues
          : createEmptyFieldValues(fields)
      );
      setIsUpdatingExisting(updating);
      setEntryDialogOpen(true);
      checkingRef.current = false;
      setChecking(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error(err);
      failCheckIn(err.response?.data?.error || 'Check-in failed');
    }
  };

  const processCheckIn = async (qrValue: string) => {
    await beginAttendeeCheckIn({ qrValue });
  };

  const handleEntrySubmit = async (fieldValues: ActivityScanFieldValues) => {
    if (!pendingScan) return;
    setEntrySubmitting(true);
    try {
      await submitScan(pendingScan, fieldValues);
    } catch (err: unknown) {
      console.error(err);
      const errorMessage =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Check-in failed';
      toast.error(errorMessage);
      playScanAudio(errorAudio.current, 'error');
    } finally {
      setEntrySubmitting(false);
    }
  };

  const confirmSelfMark = async (logId: string, attendeeName: string) => {
    setConfirmingLogId(logId);
    try {
      await axiosInstance.post(
        `/api/events/${eventId}/activities/${activityId}/pending-self-marks/${logId}/confirm`
      );
      completeCheckInSuccess(attendeeName);
    } catch (err: unknown) {
      console.error(err);
      const errorMessage =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to confirm check-in';
      failCheckIn(errorMessage);
    } finally {
      setConfirmingLogId(null);
    }
  };

  const handleScanResult = (decodedText: string) => {
    if (!shouldRunMainScannerRef.current) return;

    const now = Date.now();
    if (
      decodedText === lastScannedCode.current &&
      now - lastScannedTime.current < DUPLICATE_SCAN_WINDOW
    ) {
      return;
    }

    hapticFeedback.qrDetected();

    if (checkingRef.current) return;

    lastScannedCode.current = decodedText;
    lastScannedTime.current = now;

    void processCheckIn(decodedText);
  };

  const barcodeFieldScanRef = useRef(barcodeFieldScan);
  barcodeFieldScanRef.current = barcodeFieldScan;

  const handleBarcodeFieldCapture = useCallback((value: string) => {
    const field = barcodeFieldScanRef.current;
    if (!field) return;
    setEntryFieldValues((prev) => ({ ...prev, [field.fieldId]: value }));
    toast.success('Value captured');
    setBarcodeFieldScan(null);
  }, []);

  // Frame rendering for iOS native camera streaming
  const renderFrame = (base64: string) => {
    if (!shouldRunMainScannerRef.current) return;

    if (isRenderingRef.current) {
      frameQueueRef.current.push(base64);
      if (frameQueueRef.current.length > 3) {
        frameQueueRef.current.shift();
      }
      return;
    }

    isRenderingRef.current = true;
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);

        // Run QR detection on the canvas
        const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);
        if (imageData) {
          const qrCode = jsQR(imageData.data, imageData.width, imageData.height);
          if (qrCode?.data) {
            handleScanResult(qrCode.data);
          }
        }
      }
      isRenderingRef.current = false;

      if (frameQueueRef.current.length > 0) {
        renderFrame(frameQueueRef.current.shift()!);
      }
    };
    img.src = `data:image/jpeg;base64,${base64}`;
  };

  // iOS native frame listener
  const setupIOSListener = () => {
    const handleNativeMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'CAMERA_FRAME' && data.data) {
          renderFrame(data.data);
        } else if (data.type === 'QR_RESULT' && data.value) {
          handleScanResult(data.value);
        } else if (data.type === 'QR_SCANNER_READY') {
          setIsScanning(true);
        } else if (data.type === 'QR_SCANNER_STOPPED') {
          setIsScanning(false);
        }
      } catch (e) {
        console.error('Error parsing native message:', e);
      }
    };

    window.addEventListener('message', handleNativeMessage);

    // Request native to start scanner
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'OPEN_QR_SCANNER' }));

    return () => {
      window.removeEventListener('message', handleNativeMessage);
      window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'STOP_QR_SCANNER' }));
    };
  };

  // Monkey-patch canvas for performance
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const originalGetContext = HTMLCanvasElement.prototype.getContext as any;
    HTMLCanvasElement.prototype.getContext = function (
      contextId: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      options?: any
    ) {
      if (contextId === '2d' || contextId === '2d-context') {
        return originalGetContext.call(this, contextId, {
          ...(options as object),
          willReadFrequently: true,
        });
      }
      return originalGetContext.call(this, contextId, options);
    };

    return () => {
      HTMLCanvasElement.prototype.getContext = originalGetContext;
    };
  }, []);

  // Initialize Scanner - iOS uses native bridge when tenant allows; else html5-qrcode
  useEffect(() => {
    const isReactNative = typeof window.ReactNativeWebView !== 'undefined';
    const userAgent = navigator.userAgent || '';
    const useNativeQr =
      tenant?.iosUseNativeQrScanner !== false &&
      isReactNative &&
      /iPhone|iPad|iPod/.test(userAgent);
    setIsIOS(useNativeQr);

    if (!shouldRunMainScanner) {
      if (useNativeQr) {
        window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'STOP_QR_SCANNER' }));
      }
      setIsScanning(false);
      return;
    }

    if (useNativeQr) {
      return setupIOSListener();
    }

    let isMounted = true;
    let scannerInstance: Html5Qrcode | null = null;

    const pollAndStart = async () => {
      if (!document.getElementById(html5QrContainerId)) {
        if (isMounted) setTimeout(() => pollAndStart(), 100);
        return;
      }

      try {
        scannerInstance = new Html5Qrcode(html5QrContainerId, {
          verbose: false,
        });

        await scannerInstance.start(
          { facingMode: 'environment' },
          {
            fps: 12,
          },
          (decodedText) => {
            handleScanResult(decodedText);
          },
          (_errorMessage) => {
            //
          }
        );

        if (!isMounted) {
          await safeStopHtml5Qrcode(scannerInstance);
          scannerInstance = null;
          return;
        }

        setIsScanning(true);
      } catch (e) {
        console.error('Error starting Html5Qrcode scanner', e);
        if (isMounted) {
          setIsScanning(false);
        }
        await safeStopHtml5Qrcode(scannerInstance);
        scannerInstance = null;
      }
    };

    const timer = setTimeout(pollAndStart, 150);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      const instance = scannerInstance;
      scannerInstance = null;
      void safeStopHtml5Qrcode(instance);
    };
  }, [shouldRunMainScanner, eventId, activityId, communityId, html5QrContainerId, tenant?.iosUseNativeQrScanner]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    await processCheckIn(searchQuery);
    setSearchQuery('');
  };

  const formatFieldValue = (field: ActivityScanField, value: ActivityScanFieldValues[string]) => {
    if (field.type === 'TOGGLE') return value ? 'Yes' : 'No';
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
  };

  return (
    <div className="w-full text-foreground p-1 pb-10 flex flex-col items-center">
      <ScanEntryDialog
        open={entryDialogOpen}
        onOpenChange={(open) => {
          setEntryDialogOpen(open);
          if (!open) {
            setPendingScan(null);
            setEntryAttendee(null);
            setEntryDialogFields([]);
            setBarcodeFieldScan(null);
            setEntryFieldValues(createEmptyFieldValues(scanEntryFields));
            checkingRef.current = false;
            setChecking(false);
          }
        }}
        attendeeName={entryAttendee?.name || ''}
        attendeeEmail={entryAttendee?.email}
        fields={entryDialogFields}
        values={entryFieldValues}
        onValuesChange={setEntryFieldValues}
        isUpdatingExisting={isUpdatingExisting}
        isSubmitting={entrySubmitting}
        onSubmit={(values) => void handleEntrySubmit(values)}
        onRequestBarcodeScan={(field) => {
          setBarcodeFieldScan({ fieldId: field.id, fieldLabel: field.label });
        }}
      />
      <FieldBarcodeScannerDialog
        open={Boolean(barcodeFieldScan)}
        fieldLabel={barcodeFieldScan?.fieldLabel || 'barcode'}
        onOpenChange={(open) => {
          if (!open) setBarcodeFieldScan(null);
        }}
        onCapture={handleBarcodeFieldCapture}
      />
      <div className="w-full max-w-md mb-4 flex items-center justify-between gap-2">
        <Link to={`/${communityId}/events/${eventId}/activities`}>
          <Button
            variant="ghost"
            className="text-muted-foreground hover:text-foreground hover:bg-muted pl-0 gap-2"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Activities
          </Button>
        </Link>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Refresh"
            disabled={isRefreshing}
            onClick={() => void refreshScannerData()}
          >
            <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={voiceMuted ? 'Unmute voice' : 'Mute voice'}
            onClick={() => setVoiceMuted((prev) => !prev)}
          >
            {voiceMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={cameraClosed ? 'Open camera' : 'Close camera'}
            onClick={() => setCameraClosed((prev) => !prev)}
          >
            {cameraClosed ? <VideoOff className="h-4 w-4" /> : <Video className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <h1 className="text-3xl font-black mb-2 text-foreground tracking-tight text-center">
        {activity?.name || 'Scanner'}
      </h1>
      <p className="text-muted-foreground mb-8 text-center text-sm max-w-md">
        {activity?.description || 'Scan attendee QR codes to check them in'}
      </p>

      {pendingSelfMarks.length > 0 && (
        <div className="w-full max-w-md mb-8 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg text-foreground tracking-tight">Waiting to confirm</h3>
            <span className="bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-200 text-xs font-extrabold px-3 py-1 rounded-md tracking-wider">
              {pendingSelfMarks.length} PENDING
            </span>
          </div>
          <div className="bg-card text-card-foreground rounded-[22px] border border-amber-500/40 shadow-sm overflow-hidden divide-y divide-border/60">
            {pendingSelfMarks.map((item) => (
              <div key={item.id} className="p-4 sm:p-5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="font-bold text-foreground text-base leading-snug tracking-tight">
                    {item.attendeeName}
                  </span>
                  <p className="text-sm text-muted-foreground mt-1 break-all">{item.attendeeEmail}</p>
                  {item.attendeePhone ? (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatDisplayPhone(item.attendeePhone)}
                    </p>
                  ) : null}
                  <p className="text-xs text-muted-foreground mt-2">
                    Self-marked {formatContextualDateTime(item.scannedAt)}
                  </p>
                </div>
                <Button
                  size="sm"
                  disabled={confirmingLogId === item.id}
                  onClick={() => void confirmSelfMark(item.id, item.attendeeName)}
                  className="shrink-0"
                >
                  {confirmingLogId === item.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Confirm'
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="w-full max-w-md bg-black rounded-3xl overflow-hidden shadow-2xl relative aspect-square isolate mb-8 ring-1 ring-border border border-border">
        {/* iOS: Render frames on canvas */}
        {isIOS && !cameraClosed && <canvas ref={canvasRef} className="w-full h-full object-cover" />}

        {!isIOS && !cameraClosed && (
          <div className="absolute inset-0 min-h-0 w-full overflow-hidden rounded-[inherit] bg-black">
            <div
              id={html5QrContainerId}
              data-html5-camera-container
              className="relative h-full w-full min-h-0"
            />
          </div>
        )}

        {!cameraClosed && (
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
            <div className="w-[70%] h-[70%] border-2 border-primary/60 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.55)] dark:shadow-[0_0_0_9999px_rgba(0,0,0,0.65)]">
              <div className="absolute inset-0 border-4 border-primary/30 rounded-2xl animate-pulse" />
            </div>
          </div>
        )}

        {!isIOS && !isScanning && shouldRunMainScanner && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/85">
            <div className="text-zinc-100">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="mt-2 text-sm">Initializing camera…</p>
            </div>
          </div>
        )}

        {cameraClosed ? (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/90 gap-3">
            <VideoOff className="h-8 w-8 text-zinc-300" />
            <p className="text-sm font-medium text-zinc-100">Camera closed</p>
            <Button type="button" size="sm" variant="secondary" onClick={() => setCameraClosed(false)}>
              Open camera
            </Button>
          </div>
        ) : !shouldRunMainScanner ? (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/85">
            <p className="text-sm font-medium text-zinc-100">Scanner paused</p>
          </div>
        ) : null}
      </div>

      {(lastSuccessfulScan || checking) && (
        <p
          className={cn(
            'mt-4 font-bold text-lg flex items-center mb-8 gap-3',
            checking && !lastSuccessfulScan ? 'text-muted-foreground' : 'text-green-600'
          )}
        >
          {checking && !lastSuccessfulScan ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin shrink-0 text-primary" />
              Verifying…
            </>
          ) : (
            `Checked in: ${lastSuccessfulScan}`
          )}
        </p>
      )}

      <div className="w-full max-w-md">
        <form onSubmit={handleManualSubmit} className="relative mb-8">
          <Input
            icon={<Search className="h-5 w-5 text-muted-foreground" />}
            uiSize="lg"
            placeholder="Search by name, email"
            className="h-14 rounded-[20px] border border-border/80 bg-card text-base sm:text-lg shadow-sm focus-visible:border-primary focus-visible:ring-primary/20 focus-visible:ring-offset-0 focus-visible:ring-offset-background placeholder:text-muted-foreground/70"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
          />
          <Button type="submit" className="sr-only">
            Check In
          </Button>

          {/* Auto-Complete Search Dropdown */}
          {(debouncedSearchQuery.length >= 1 || isSearchLoading) && searchQuery.length > 0 && (
            <div className="absolute top-[calc(100%+0.5rem)] left-0 right-0 z-50 bg-popover text-popover-foreground border border-border rounded-2xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2">
              {isSearchLoading ? (
                <div className="flex items-center justify-center p-6 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Searching database...
                </div>
              ) : searchResults && searchResults.length > 0 ? (
                <div className="max-h-[300px] overflow-y-auto">
                  {searchResults.map(
                    (res: {
                      id: string;
                      name: string;
                      email: string;
                      phone?: string;
                      type: 'TICKET' | 'WALKIN';
                    }) => (
                      <Button
                        key={res.id}
                        type="button"
                        disabled={checking || checkingRef.current}
                        variant="ghost"
                        className="w-full h-auto text-left p-4 hover:bg-muted/80 focus:bg-muted transition-colors border-b border-border last:border-0 flex items-center justify-between group disabled:opacity-50 disabled:cursor-not-allowed rounded-none"
                        onClick={async () => {
                          setSearchQuery('');
                          await processCheckIn(res.id);
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="font-bold text-foreground group-hover:text-primary transition-colors">
                            {res.name}
                          </span>
                          <span className="text-xs text-muted-foreground block line-clamp-1">
                            {res.email}{' '}
                            {res.phone ? `• ${formatDisplayPhone(res.phone)}` : ''}
                          </span>
                        </div>
                        <Badge
                          variant={res.type === 'TICKET' ? 'default' : 'secondary'}
                          className="text-[10px] shrink-0"
                        >
                          {res.type === 'TICKET' ? 'Has Ticket' : 'Walk-in'}
                        </Badge>
                      </Button>
                    )
                  )}
                </div>
              ) : (
                <div className="p-6 text-center text-muted-foreground text-sm font-medium">
                  No attendees matching "{debouncedSearchQuery}"
                </div>
              )}
            </div>
          )}
        </form>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-xl text-foreground tracking-tight">Recent scans</h3>
            <span className="bg-[#f0f2f5] dark:bg-muted text-foreground text-xs font-extrabold px-3 py-1 rounded-md tracking-wider">
              {logs.length} SCANNED
            </span>
          </div>
          {logs && logs.length > 0 ? (
            <div className="bg-card text-card-foreground rounded-[22px] border border-border/80 shadow-sm overflow-hidden divide-y divide-border/60">
              {logs.map((log) => (
                <div key={log.id} className="p-4 sm:p-5">
                  <div>
                    <span className="font-bold text-foreground text-base sm:text-lg leading-snug tracking-tight">
                      {log.attendeeName}
                    </span>
                    <span className="float-right shrink-0 text-right text-xs text-muted-foreground bg-[#f0f2f5] dark:bg-muted px-2.5 py-1 rounded-md font-medium whitespace-nowrap ml-auto">
                      {formatContextualDateTime(log.scannedAt)}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground mt-1 break-all font-normal">
                    {log.attendeeEmail}
                  </p>
                  {scanEntryFields.length > 0 && log.fieldValues ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {scanEntryFields.map((field) => (
                        <Badge key={field.id} variant="outline" className="text-[10px] font-normal">
                          {field.label}:{' '}
                          {formatFieldValue(field, log.fieldValues?.[field.id] ?? null)}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground bg-muted/30 rounded-2xl border border-dashed border-border">
              No scans yet for this activity
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
